/* Parlanti size stack (see snippets/product-parlanti-size.liquid).
   Ready-to-wear: Height/Calf are line-item properties (no priced Fit axis to derive,
   unlike product-deniro-size.js). This file owns three jobs:
     1. derive the hidden "Parlanti Leg Code" property (calf letter + height suffix — "MH+")
     2. the yours-vs-this-size fit grid (exact per-foot values from the chart metaobject's
        size_values, which key heights by RANGE NAME for Parlanti — not De Niro codes)
     3. ATC gating: the theme form is novalidate, so `required` alone does nothing.
   Reuses the rbc-dns__* styles from product-deniro-size.css (loaded by the snippet). */

(() => {
  /* No early exit when the page has no selector yet: the add-to-cart gate and the readiness signal below must exist
     for one mounted later (quick view), and a module is only evaluated once. */
  const roots = document.querySelectorAll('[data-parlanti-size]');

  let ffCfg = null;
  function loadCfg() {
    if (ffCfg !== null) return ffCfg;
    ffCfg = false;
    try {
      const el = document.getElementById('rbc-fitfinder-config');
      const cfg = el && JSON.parse(el.textContent);
      if (cfg && cfg.chart && cfg.chart.found) ffCfg = cfg;
    } catch (err) { /* fit line stays hidden */ }
    return ffCfg;
  }
  const parseRange = (s) => {
    const m = String(s).match(/([\d.]+)\s*-\s*([\d.]+)/);
    return m ? { lo: parseFloat(m[1]), hi: parseFloat(m[2]) } : null;
  };
  const rangeName = (s) => {
    const m = String(s).match(/\(([^)]+)\)/);
    return m ? m[1] : '';
  };
  function modFor(modStr, shoe) {
    if (!modStr || isNaN(shoe)) return 0;
    for (const part of String(modStr).split(';')) {
      const kv = part.split('|');
      if (kv.length === 2 && parseFloat(kv[0]) === shoe) return parseFloat(kv[1]) || 0;
    }
    return 0;
  }
  // Parlanti gambale = calf letter + height suffix. Height option values carry the
  // suffix in parens ("Tall (H)"); base height ("Short") has none.
  function legCode(heightVal, calfVal) {
    if (!heightVal || !calfVal) return '';
    const m = heightVal.match(/\(([^)]+)\)/);
    return calfVal + (m ? m[1] : '');
  }

  function selectedShoe(root, cfg) {
    // Foot value from the variant machinery: radio (swatch picker) or select (dropdown picker).
    const scope = root.parentElement.querySelector('product-variant-selector') ||
      document.querySelector('product-variant-selector');
    const candidates = [];
    if (scope) {
      scope.querySelectorAll('input[type="radio"][data-option-index]:checked').forEach((r) => candidates.push(r.value));
      scope.querySelectorAll('select').forEach((s) => { if (s.value) candidates.push(s.value); });
    }
    for (const v of candidates) {
      if ((cfg.footSizes || []).indexOf(String(v).trim()) !== -1) {
        const m = String(v).match(/\d+(\.\d+)?/);
        if (m) {
          let shoe = parseFloat(m[0]);
          return cfg.chart.hasHalfSizes ? Math.round(shoe * 2) / 2 : Math.round(shoe);
        }
      }
    }
    return NaN;
  }

  function saved(root) {
    try {
      const s = JSON.parse(localStorage.getItem('rbcFitFinder:' + root.dataset.productId)
        || localStorage.getItem('rbcFitFinder:profile') || '{}').values || null;
      if (!s) return null;
      const calf = parseFloat(s.calfSize), leg = parseFloat(s.legHeight);
      return (isNaN(calf) && isNaN(leg)) ? null : { calf, leg };
    } catch (err) { return null; }
  }

  function nominal(cfg, kind, key, shoe) {
    const vals = cfg.chart.values;
    const table = vals && (kind === 'leg' ? vals.heights : vals.calves);
    const v = table && table[key] && table[key][String(shoe)];
    return (v || v === 0) ? v : null;
  }

  function update(root) {
    const h = root.querySelector('[data-pps-height]');
    const c = root.querySelector('[data-pps-calf]');
    const codeInput = root.querySelector('[data-pps-legcode]');
    const code = legCode(h && h.value, c && c.value);
    if (codeInput) {
      codeInput.value = code;
      codeInput.disabled = !code; // disabled inputs never submit — no empty property on the order
    }
    const line = root.querySelector('[data-pps-chartline]');
    if (!line) return;
    const cfg = loadCfg();
    const m = cfg && saved(root);
    const shoe = cfg ? selectedShoe(root, cfg) : NaN;
    line.replaceChildren();
    const rows = [];
    if (cfg && !isNaN(shoe)) {
      if (h && h.selectedIndex > 0 && m && !isNaN(m.leg)) {
        const name = rangeName(cfg.chart.legRanges[h.selectedIndex - 1] || '') ||
          (h.value.split(' (')[0]);
        const nom = nominal(cfg, 'leg', name, shoe);
        const band = parseRange(cfg.chart.legRanges[h.selectedIndex - 1] || '');
        const adj = m.leg + modFor(cfg.mods && cfg.mods.height, shoe);
        rows.push(row('Height', m.leg, nom, band, adj, 'leg'));
      }
      if (c && c.value && m && !isNaN(m.calf)) {
        const nom = nominal(cfg, 'calf', c.value, shoe);
        const bandStr = (cfg.chart.calfRanges || []).find((s) => rangeName(s) === c.value);
        const band = bandStr && parseRange(bandStr);
        const adj = m.calf + modFor(cfg.mods && cfg.mods.calf, shoe);
        rows.push(row('Calf', m.calf, nom, band, adj, 'calf'));
      }
    }
    const usable = rows.filter(Boolean);
    if (usable.length) {
      const grid = document.createElement('span');
      grid.className = 'rbc-dns__fitgrid';
      ['', 'Yours', 'This size'].forEach((t) => {
        const s = document.createElement('span');
        s.className = 'rbc-dns__fith';
        s.textContent = t;
        grid.appendChild(s);
      });
      usable.forEach((e) => {
        const dim = document.createElement('span');
        dim.className = 'rbc-dns__fitdim';
        dim.textContent = e.label;
        grid.appendChild(dim);
        const yours = document.createElement('span');
        yours.className = 'rbc-dns__fitval';
        yours.textContent = isNaN(e.raw) ? '—' : e.raw + ' cm';
        grid.appendChild(yours);
        const size = document.createElement('span');
        size.className = 'rbc-dns__fitval';
        size.textContent = (e.nominal || e.nominal === 0) ? e.nominal + ' cm' : '—';
        if (e.note) {
          const n = document.createElement('span');
          n.className = 'rbc-dns__fitnote';
          n.textContent = e.note;
          size.appendChild(n);
        }
        grid.appendChild(size);
      });
      line.appendChild(grid);
    }
    line.hidden = !usable.length;
  }

  /* Honest qualifier only when the mod-adjusted measurement sits outside the band —
     same frame the Fit Finder verdicts use, so the two surfaces agree. Any quoted
     comparison is between the two numbers ON SHOW (yours vs the size's exact value). */
  function row(label, raw, nom, band, adjusted, kind) {
    let note = '';
    if (band && !isNaN(adjusted)) {
      if (adjusted > band.hi) note = kind === 'calf' ? 'on the snug side' : 'on the short side';
      else if (adjusted < band.lo) note = kind === 'calf' ? 'on the roomy side' : 'on the tall side';
    }
    return { label, raw, nominal: nom, note };
  }

  /* Gate add-to-cart: same pattern as product-deniro-size.js — document-level capture
     + stopImmediatePropagation so the theme's fetch-cart handler never sees an
     incomplete submit, and the button's !loading state (set at click time, cleared
     only by cart:update/cart:error, which never arrive for a blocked submit) is
     unstuck by hand. The form is novalidate; element-level checkValidity still works. */
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form.matches || !form.matches('form[action*="/cart/add"]')) return;
    const formId = form.getAttribute('id');
    // Live lookup, NOT the module-load snapshot: the theme can inject quick-view
    // content after this module evaluated — a frozen NodeList would let those
    // forms submit ungated (Codex P1).
    const root = Array.from(document.querySelectorAll('[data-parlanti-size]'))
      .find((r) => r.dataset.formId === formId);
    if (!root) return;
    // Derive at submit time, unconditionally: a late-injected root may never have
    // had a change event, leaving the Leg Code input empty+disabled even though
    // its selects validate (Codex P1 round 2). update() is idempotent.
    update(root);
    const invalid = [];
    root.querySelectorAll('select[required]').forEach((c) => {
      const bad = !c.checkValidity();
      c.classList.toggle('rbc-mtm__invalid', bad);
      if (bad) invalid.push(c);
    });
    if (invalid.length) {
      e.preventDefault();
      e.stopImmediatePropagation();
      form.querySelectorAll('[data-to-cart]').forEach((b) => b.classList.remove('!loading'));
      invalid[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
      invalid[0].focus({ preventScroll: true });
    }
  }, true);

  // ONE delegated change listener, live-queried roots: covers our selects, the
  // variant machinery (foot changes move the fit line), and any root injected
  // after module load. Late roots paint their fit line on first interaction.
  document.addEventListener('change', (ev) => {
    const t = ev.target;
    if (!t || !t.closest) return;
    const own = t.closest('[data-parlanti-size]');
    if (own) { update(own); return; }
    if (t.closest('product-variant-listener')) {
      document.querySelectorAll('[data-parlanti-size]').forEach(update);
    }
  });
  roots.forEach(update);

  /* The add-to-cart check above is in place: tell the inline guard (rbc-measure-guard.js), which until now has been
     keeping any press of Add to cart on this form, to replay it. */
  window.rbcSizeReady = Object.assign(window.rbcSizeReady || {}, { parlanti: true });
  document.dispatchEvent(new CustomEvent('rbc:size:ready'));
})();
